// src/components/PageLayout.js
import React from "react";

/**
 * PageLayout
 * 요구사항 정리:
 * 1) 메뉴바와 아래 페이지 폭 동일(PC: 1200 고정, Mobile: full)
 * 2) Mobile: 좌우 꽉참
 * 3) PC: max-w 1200 고정
 * 4) 카페/캔들 배경은 동일 구조, 색만 다르게
 * 5) 배경과 카드 사이 5px 정도 여백(둥근 모서리 예쁘게)
 * 6) 배경은 화면 top부터, 첫 카드가 메뉴바와 너무 멀지 않게
 *
 * 사용 예:
 * <PageLayout variant="cafe">...</PageLayout>
 * <PageLayout variant="candle">...</PageLayout>
 *
 * ✅ PC에서만 전체폭이 필요할 때:
 * <PageLayout fullWidthPc>...</PageLayout>
 */
export default function PageLayout({
  variant = "cafe", // "cafe" | "candle"
  children,
  className = "",
  contentClassName = "",
  fullWidthPc = false, // ✅ 기본값 false: 기존과 100% 동일 / true일 때만 PC에서 전체폭
  topPadClassName, // ✅ 상단 패딩 override (예: "pt-2 md:pt-3")
}) {
  const isCafe = variant === "cafe";

  // ✅ 배경(카페는 밝게, 캔들은 어둡게 / 둘 다 은은한 그라데이션)
  const bgClass = isCafe
    ? "bg-gradient-to-b from-amber-50 via-rose-50 to-sky-50 text-slate-900"
    : "bg-gradient-to-b from-[#0e0f14] via-[#0b0c10] to-[#07070b] text-white";

  // ✅ 상단 은은한 조명(둘 다 같은 구조)
  const glowTop = isCafe
    ? "bg-[radial-gradient(ellipse_at_top,rgba(255,255,255,0.85),transparent_60%)]"
    : "bg-[radial-gradient(ellipse_at_top,rgba(217,179,106,0.14),transparent_65%)]";

  /**
   * ✅ Navbar/BottomNav fixed 보정
   * - 새 매뉴바 기준: 상단 56/64px + 약간 여유 / 하단 64/72px + 약간 여유
   * - CSS 변수(--top-nav-h, --bottom-nav-h) 기반으로 일관 적용
   */
  const topPad =
    topPadClassName ||
    "pt-[calc(var(--top-nav-h)+12px)]";

  // ✅ 컨텐츠 폭:
  // - 기본(기존): PC max-w-[1200px]
  // - fullWidthPc=true: PC에서도 제한 없이 전체폭
  //   (모바일은 원래 w-full이라 변화 없음)
  const containerClass = fullWidthPc
    ? "mx-auto w-full max-w-none"
    : "mx-auto w-full max-w-[1200px]";

  return (
    <div className={`min-h-screen w-full ${bgClass} ${className}`}>
      {/* ✅ 배경이 화면 최상단부터 시작 */}
      <div className="relative">
        {/* ✅ 상단 글로우(페이지 전체 배경 위에 자연스럽게) */}
        <div className="pointer-events-none absolute inset-x-0 top-0 h-[520px] opacity-100">
          <div className={`absolute inset-0 ${glowTop}`} />
        </div>

        {/* ✅ 실제 컨텐츠 영역 — 하단 네비바 위로 컨텐츠가 정확히 끝나도록 padding-bottom */}
        <div
          className={`relative w-full ${topPad}`}
          style={{ paddingBottom: "calc(var(--bottom-nav-h) + 24px)" }}
        >
          {/* ✅ 메뉴바와 페이지 폭 통일 */}
          <div className={containerClass}>
            {/* ✅ 배경과 카드 사이 5px 여백 */}
            <div className={`px-[5px] ${contentClassName}`}>{children}</div>
          </div>
        </div>
      </div>
    </div>
  );
}
